import { create } from 'zustand';
import { immer } from 'zustand/middleware/immer';
import type {
  Filter,
  OutputConfig,
  AnalyticsQuery,
  QueryResult,
  VisualizationType,
  UIState,
  QueryMode,
} from '../types/analytics.types';
import { generateFilterId } from '../lib/formatters';

interface AnalyticsState {
  mode: QueryMode;
  naturalLanguageInput: string;
  filters: Filter[];
  filterLogic: 'AND' | 'OR';
  outputConfig: OutputConfig;

  uiState: UIState;

  currentQuery: AnalyticsQuery | null;
  queryResult: QueryResult | null;
  selectedVisualization: VisualizationType | null;

  selectedPatientIds: string[];
  patientListPage: number;
  patientListPageSize: number;

  previewCount: number | null;
  previewLoading: boolean;

  error: string | null;
}

interface AnalyticsActions {
  setMode: (mode: QueryMode) => void;
  setNaturalLanguageInput: (input: string) => void;

  addFilter: (filter: Omit<Filter, 'id'>) => void;
  updateFilter: (id: string, updates: Partial<Filter>) => void;
  removeFilter: (id: string) => void;
  clearFilters: () => void;
  setFilterLogic: (logic: 'AND' | 'OR') => void;

  setOutputConfig: (config: Partial<OutputConfig>) => void;

  setUIState: (state: UIState) => void;
  setCurrentQuery: (query: AnalyticsQuery | null) => void;
  setQueryResult: (result: QueryResult | null) => void;
  setSelectedVisualization: (vis: VisualizationType) => void;

  togglePatientSelection: (id: string) => void;
  selectAllPatients: (ids: string[]) => void;
  clearPatientSelection: () => void;
  setPatientListPage: (page: number) => void;

  setPreviewCount: (count: number | null) => void;
  setPreviewLoading: (loading: boolean) => void;

  setError: (error: string | null) => void;

  buildQueryFromState: () => AnalyticsQuery;
  loadQueryIntoBuilder: (query: AnalyticsQuery) => void;

  reset: () => void;
}

const initialState: AnalyticsState = {
  mode: 'natural_language',
  naturalLanguageInput: '',
  filters: [],
  filterLogic: 'AND',
  outputConfig: { type: 'count' },

  uiState: 'idle',

  currentQuery: null,
  queryResult: null,
  selectedVisualization: null,

  selectedPatientIds: [],
  patientListPage: 1,
  patientListPageSize: 25,

  previewCount: null,
  previewLoading: false,

  error: null,
};

export const useAnalyticsStore = create<AnalyticsState & AnalyticsActions>()(
  immer((set, get) => ({
    ...initialState,

    setMode: (mode) =>
      set((state) => {
        state.mode = mode;
      }),

    setNaturalLanguageInput: (input) =>
      set((state) => {
        state.naturalLanguageInput = input;
      }),

    addFilter: (filter) =>
      set((state) => {
        state.filters.push({
          ...filter,
          id: generateFilterId(),
        });
      }),

    updateFilter: (id, updates) =>
      set((state) => {
        const index = state.filters.findIndex((f) => f.id === id);
        if (index !== -1) {
          Object.assign(state.filters[index], updates);
        }
      }),

    removeFilter: (id) =>
      set((state) => {
        state.filters = state.filters.filter((f) => f.id !== id);
      }),

    clearFilters: () =>
      set((state) => {
        state.filters = [];
      }),

    setFilterLogic: (logic) =>
      set((state) => {
        state.filterLogic = logic;
      }),

    setOutputConfig: (config) =>
      set((state) => {
        Object.assign(state.outputConfig, config);
      }),

    setUIState: (uiState) =>
      set((state) => {
        state.uiState = uiState;
      }),

    setCurrentQuery: (query) =>
      set((state) => {
        state.currentQuery = query;
      }),

    setQueryResult: (result) =>
      set((state) => {
        state.queryResult = result;
      }),

    setSelectedVisualization: (vis) =>
      set((state) => {
        state.selectedVisualization = vis;
      }),

    togglePatientSelection: (id) =>
      set((state) => {
        const index = state.selectedPatientIds.indexOf(id);
        if (index === -1) {
          state.selectedPatientIds.push(id);
        } else {
          state.selectedPatientIds.splice(index, 1);
        }
      }),

    selectAllPatients: (ids) =>
      set((state) => {
        state.selectedPatientIds = ids;
      }),

    clearPatientSelection: () =>
      set((state) => {
        state.selectedPatientIds = [];
      }),

    setPatientListPage: (page) =>
      set((state) => {
        state.patientListPage = page;
      }),

    setPreviewCount: (count) =>
      set((state) => {
        state.previewCount = count;
      }),

    setPreviewLoading: (loading) =>
      set((state) => {
        state.previewLoading = loading;
      }),

    setError: (error) =>
      set((state) => {
        state.error = error;
      }),

    buildQueryFromState: () => {
      const state = get();
      return {
        title: 'Custom Query',
        filters: state.filters,
        filterLogic: state.filterLogic,
        output: state.outputConfig,
      };
    },

    loadQueryIntoBuilder: (query) =>
      set((state) => {
        state.filters = query.filters;
        state.filterLogic = query.filterLogic;
        state.outputConfig = query.output;
        state.mode = 'builder';
      }),

    reset: () => set(initialState),
  }))
);
